import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Shield, Loader2, CheckCircle2, AlertCircle, KeyRound, History,
  ArrowRight, Eye,
} from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { useAdminToken } from "@/context/AdminTokenContext";

// Hotfix v14b — Admin-UI für POST /api/admin/bootstrap-promote.
// Admin-only Tool, um existierende User auf license_tier + role hochzustufen.
// Häufige Use-Cases: Beta-Tester onboarden, Ablefy-Webhook-Lücken überbrücken,
// Power-Master-Accounts für Testing einrichten.
//
// Sprint V101.d (Sa 20.09.2026) — Refactor in AdminLayout:
//   • Admin-Guard läuft im Layout
//   • Token stammt aus AdminTokenContext (shared)
//   • Lokales Token-Feld/Show-Toggle/RememberCheckbox entfernt

const TIERS = [
  { value: "free", label: "Free" },
  { value: "pro_solo_monthly", label: "Pro Solo Monthly" },
  { value: "pro_solo_annual", label: "Pro Solo Annual" },
  { value: "power_paket_onetime", label: "PowerPaket (Lifetime)" },
];

const ROLES = [
  { value: "user", label: "User" },
  { value: "admin", label: "Admin" },
];

const HISTORY_STORAGE_KEY = "f4s_admin_bootstrap_history";

export default function AdminBootstrap() {
  const { token } = useAdminToken();
  const [email, setEmail] = useState("");
  const [tier, setTier] = useState("power_paket_onetime");
  const [role, setRole] = useState("user");
  const [clearTraces, setClearTraces] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);

  // Phase 1 (Feb 2026) — License-Audit-Diagnose
  const [auditEmail, setAuditEmail] = useState("");
  const [auditing, setAuditing] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [auditError, setAuditError] = useState("");

  useEffect(() => {
    // History aus localStorage laden
    try {
      const h = JSON.parse(localStorage.getItem(HISTORY_STORAGE_KEY) || "[]");
      if (Array.isArray(h)) setHistory(h);
    } catch (_e) { /* ignore */ }
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    const trimmed = email.trim().toLowerCase();
    const tk = token.trim();
    if (!trimmed || !trimmed.includes("@")) {
      setError("Bitte gültige E-Mail-Adresse eingeben.");
      return;
    }
    if (!tk || tk.length < 32) {
      setError("Token fehlt oder ist zu kurz (≥32 Zeichen).");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(
        "/admin/bootstrap-promote",
        {
          email: trimmed,
          license_tier: tier,
          role,
          clear_customer_traces: clearTraces,
        },
        { headers: { "X-Retention-Token": tk } },
      );
      setResult(data);
      // History: letzte 5 Promotes
      try {
        const entry = {
          ts: new Date().toISOString(),
          email: trimmed,
          tier,
          role,
          before: data.before,
        };
        const next = [entry, ...history].slice(0, 5);
        setHistory(next);
        localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(next));
      } catch (_e) { /* ignore */ }
      setEmail("");
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.response?.data?.detail?.code;
      if (status === 404 && code === "user_not_found") {
        setError(
          `User ${trimmed} existiert nicht. Bitte zuerst per Magic-Link in Production registrieren.`,
        );
      } else if (status === 404) {
        setError("Token ungültig oder Endpoint nicht verfügbar (404).");
      } else if (status === 400 && code) {
        setError(`Validierung fehlgeschlagen: ${code}`);
      } else {
        setError(
          err?.response?.data?.detail?.message ||
            `Fehler ${status || "?"} — bitte erneut versuchen.`,
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onAudit = async (e) => {
    e?.preventDefault?.();
    setAuditError("");
    setAuditResult(null);
    const trimmed = (auditEmail || email).trim().toLowerCase();
    const tk = token.trim();
    if (!trimmed || !trimmed.includes("@")) {
      setAuditError("Bitte gültige E-Mail-Adresse eingeben.");
      return;
    }
    if (!tk || tk.length < 32) {
      setAuditError("Token fehlt oder ist zu kurz (≥32 Zeichen).");
      return;
    }
    setAuditing(true);
    try {
      const { data } = await api.get(
        `/admin/license/audit?email=${encodeURIComponent(trimmed)}`,
        { headers: { "X-Retention-Token": tk } },
      );
      setAuditResult(data);
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.response?.data?.detail?.code;
      if (status === 404 && code === "user_not_found") {
        setAuditError(`User ${trimmed} existiert nicht in der DB.`);
      } else if (status === 404) {
        setAuditError("Token ungültig oder Endpoint nicht verfügbar (404).");
      } else {
        setAuditError(`Fehler ${status || "?"} — bitte erneut versuchen.`);
      }
    } finally {
      setAuditing(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-2xl pb-24" data-testid="admin-bootstrap-page">
          {/* Header */}
          <header className="mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 text-amber-800 text-xs font-bold uppercase tracking-wider">
              <Shield className="w-3.5 h-3.5" /> Admin-Tool
            </div>
            <h1
              data-testid="admin-bootstrap-headline"
              className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-[var(--fts-text-primary)]"
            >
              Bootstrap-Promote
            </h1>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              Existierenden User auf License-Tier + Role hochstufen.
              Beta-Tester onboarden · Power-Master-Accounts · Ablefy-Webhook-Lücken überbrücken.
              <strong className="ml-1 text-slate-800">User muss vorher per Magic-Link registriert sein.</strong>
              <span className="block mt-1 text-xs text-slate-500">Der Admin-Token stammt aus der Sidebar-Fußzeile.</span>
            </p>
          </header>

          {/* Form */}
          <form
            onSubmit={onSubmit}
            data-testid="admin-bootstrap-form"
            className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm space-y-4"
          >
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
                User-E-Mail
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                placeholder="mastermind@laimisch.de"
                data-testid="admin-bootstrap-email"
                className="w-full h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#4563BB]"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
                  License-Tier
                </label>
                <select
                  value={tier}
                  onChange={(ev) => setTier(ev.target.value)}
                  data-testid="admin-bootstrap-tier"
                  className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#4563BB]"
                >
                  {TIERS.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
                  Role
                </label>
                <select
                  value={role}
                  onChange={(ev) => setRole(ev.target.value)}
                  data-testid="admin-bootstrap-role"
                  className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#4563BB]"
                >
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Sprint V101.e (20.09.2026) — Kunden-Spuren-Bereinigung */}
            <label className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 cursor-pointer hover:border-slate-300">
              <input
                type="checkbox"
                checked={clearTraces}
                onChange={(ev) => setClearTraces(ev.target.checked)}
                data-testid="admin-bootstrap-clear-traces"
                className="mt-0.5 h-4 w-4 accent-[#F3651D]"
              />
              <span className="text-xs leading-relaxed text-slate-700">
                <strong className="text-slate-900">Kunden-Spuren entfernen</strong> — setzt <code className="text-[10px] bg-white px-1 rounded">powerpaket_lifetime</code>, <code className="text-[10px] bg-white px-1 rounded">is_premium</code> auf <code className="text-[10px] bg-white px-1 rounded">false</code> und unsettet <code className="text-[10px] bg-white px-1 rounded">product_id</code>, <code className="text-[10px] bg-white px-1 rounded">subscription_id</code>, <code className="text-[10px] bg-white px-1 rounded">powerpaket_granted_at</code>, <code className="text-[10px] bg-white px-1 rounded">powerpaket_source</code>, <code className="text-[10px] bg-white px-1 rounded">powerpaket_purchased_at</code>.
                Für <em>internes Admin-Konto</em>, das versehentlich als Kunde markiert wurde. Nur mit Tier=Free + Role=Admin sinnvoll.
              </span>
            </label>

            {/* Token stammt aus AdminLayout-Sidebar (shared). */}
            {!token && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2">
                <KeyRound className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>Kein Admin-Token gesetzt — trage ihn in der Sidebar-Fußzeile ein.</span>
              </p>
            )}

            {error && (
              <p data-testid="admin-bootstrap-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-bootstrap-submit"
              className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-xl bg-[#0F172A] text-white text-sm font-bold hover:bg-slate-800 disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Promote läuft…
                </>
              ) : (
                <>
                  <ArrowRight className="w-4 h-4" /> User hochstufen
                </>
              )}
            </button>
          </form>

          {/* Result */}
          {result && (
            <motion.section
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              data-testid="admin-bootstrap-result"
              className="mt-6 rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-5 sm:p-6"
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <h2 className="text-base font-extrabold text-emerald-900">
                    Promote erfolgreich
                  </h2>
                  <p className="mt-1 text-sm text-emerald-900/80 break-all">
                    {result.email} <span className="text-xs text-emerald-700">(id: {result.user_id})</span>
                  </p>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <BeforeAfterBlock label="Vorher" data={result.before} variant="before" />
                    <BeforeAfterBlock label="Nachher" data={result.after} variant="after" />
                  </div>
                  {Array.isArray(result.cleared_traces) && result.cleared_traces.length > 0 && (
                    <div className="mt-3 rounded-lg border border-emerald-200 bg-white p-3 text-xs" data-testid="admin-bootstrap-cleared-traces">
                      <p className="font-bold text-emerald-800 uppercase tracking-wider text-[10px] mb-1">Bereinigt</p>
                      <p className="text-slate-700">{result.cleared_traces.join(", ")}</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.section>
          )}

          {/* License-Audit (Phase 1, Feb 2026) — Diagnose-Tool für Tier-Inkonsistenzen */}
          <section
            className="mt-8 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm"
            data-testid="admin-license-audit-section"
          >
            <h2 className="flex items-center gap-2 text-base font-extrabold text-[var(--fts-text-primary)]">
              <Eye className="w-4 h-4 text-amber-600" />
              License-Audit
            </h2>
            <p className="mt-1 text-xs text-slate-600 leading-relaxed">
              Prüft für einen User alle Felder + alle Callsites auf Konsistenz.
              Nutzt den gleichen Token wie Promote.
            </p>
            <form onSubmit={onAudit} className="mt-4 flex flex-col sm:flex-row gap-3" data-testid="admin-license-audit-form">
              <input
                type="email"
                value={auditEmail}
                onChange={(e) => setAuditEmail(e.target.value)}
                placeholder={email || "user@example.com"}
                data-testid="admin-license-audit-email-input"
                className="flex-1 h-10 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
              />
              <button
                type="submit"
                disabled={auditing}
                data-testid="admin-license-audit-submit"
                className="h-10 px-4 inline-flex items-center justify-center gap-2 rounded-xl bg-amber-600 text-white text-sm font-bold hover:bg-amber-700 disabled:opacity-50"
              >
                {auditing ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Audit läuft…</>
                ) : (
                  <><Eye className="w-4 h-4" /> Audit ausführen</>
                )}
              </button>
            </form>
            {auditError && (
              <p data-testid="admin-license-audit-error" className="mt-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{auditError}</span>
              </p>
            )}
            {auditResult && (
              <div className="mt-5 space-y-4" data-testid="admin-license-audit-result">
                {/* Premium-Verdict */}
                <div className={`rounded-xl p-4 border-2 ${
                  auditResult.license_state?.is_premium
                    ? "border-emerald-300 bg-emerald-50"
                    : "border-slate-300 bg-slate-50"
                }`}>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Premium-Verdict</p>
                  <p className={`mt-1 text-lg font-extrabold ${
                    auditResult.license_state?.is_premium ? "text-emerald-700" : "text-slate-700"
                  }`}>
                    {auditResult.license_state?.is_premium ? "PRO" : "FREE"}
                    {auditResult.license_state?.is_lifetime && (
                      <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Lifetime</span>
                    )}
                  </p>
                  <p className="mt-1 text-xs text-slate-600">{auditResult.license_state?.premium_reason}</p>
                </div>

                {/* Konsistenz-Check */}
                <div className={`rounded-xl p-4 border ${
                  auditResult.consistent
                    ? "border-emerald-200 bg-emerald-50/50"
                    : "border-red-300 bg-red-50"
                }`}>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Callsite-Konsistenz: {auditResult.consistent ? "OK ✅" : "INKONSISTENT ❌"}
                  </p>
                  <ul className="mt-2 space-y-1 text-xs font-mono">
                    {Object.entries(auditResult.callsite_check || {}).map(([k, v]) => (
                      <li key={k} className="flex justify-between gap-2">
                        <span className="text-slate-600 truncate">{k}</span>
                        <span className={v ? "text-emerald-700 font-bold" : "text-slate-500"}>{String(v)}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Raw Fields */}
                <details className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <summary className="cursor-pointer text-xs font-bold uppercase tracking-wider text-slate-500">
                    Rohe DB-Felder
                  </summary>
                  <ul className="mt-3 space-y-1 text-xs font-mono">
                    {Object.entries(auditResult.raw_fields || {}).map(([k, v]) => (
                      <li key={k} className="flex justify-between gap-2">
                        <span className="text-slate-600 truncate">{k}</span>
                        <span className="text-slate-800">{v === null || v === undefined ? "—" : String(v)}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              </div>
            )}
          </section>

          {/* History */}
          {history.length > 0 && (
            <section className="mt-8" data-testid="admin-bootstrap-history">
              <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">
                <History className="w-3.5 h-3.5" />
                Letzte Promotes (lokal, max. 5)
              </h3>
              <ul className="space-y-2">
                {history.map((h, i) => (
                  <li
                    key={`${h.ts}-${i}`}
                    data-testid={`admin-bootstrap-history-item-${i}`}
                    className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-slate-100 bg-white text-xs"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-mono text-slate-700 truncate">{h.email}</p>
                      <p className="text-slate-400">{new Date(h.ts).toLocaleString("de-DE")}</p>
                    </div>
                    <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-semibold whitespace-nowrap">
                      {h.tier} · {h.role}
                    </span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => {
                  setHistory([]);
                  try { localStorage.removeItem(HISTORY_STORAGE_KEY); } catch (_e) { /* ignore */ }
                }}
                data-testid="admin-bootstrap-history-clear"
                className="mt-3 text-xs text-slate-500 hover:text-red-600 underline underline-offset-2"
              >
                History löschen
              </button>
            </section>
          )}
      </div>
    </AdminLayout>
  );
}

function BeforeAfterBlock({ label, data, variant }) {
  const tone = variant === "after" ? "border-emerald-300 bg-white" : "border-slate-200 bg-slate-50";
  return (
    <div className={`rounded-lg border ${tone} p-3 text-xs`}>
      <p className="font-bold text-slate-500 uppercase tracking-wider text-[10px] mb-1.5">{label}</p>
      <p className="text-slate-700"><span className="text-slate-400">tier:</span> {data?.license_tier || "—"}</p>
      <p className="text-slate-700"><span className="text-slate-400">role:</span> {data?.role || "—"}</p>
      <p className="text-slate-700"><span className="text-slate-400">status:</span> {data?.license_status || "—"}</p>
    </div>
  );
}
